import React, { useCallback, useEffect, useMemo, useState } from "react";
import Swal from "sweetalert2";
import {
  useProdesk,
  prodeskConsultarCatalogo,
  PRODESK_CATALOGOS_GENERALES,
  buildProdeskParticipant,
  buildProdeskAddress,
  buildProdeskEmployment,
  buildProdeskCreditsBureau,
  buildProdeskPropuesta,
} from "./prodeskApi";
import "./prodesk.css";

// ==========================================================
// Babilonia + NR / Prodesk (Originación) - Flujo de PRUEBA
// ----------------------------------------------------------
// Página autocontenida para validar de punta a punta el flujo de
// Originación (Token -> Catálogos -> Propuesta -> Firma NAAT) contra
// UAT. No depende de crearventa.js; reproduce el mismo lenguaje
// visual (nr-card, nr-btn, form-control nr-control, etc.) para que
// se vea consistente con el resto de la app, pero define sus propios
// componentes chicos (Icon, SectionCard, TextInput...) para poder
// vivir en un archivo separado.
//
// Este componente es para QA/pruebas, no para el flujo final del
// asesor: expone el JSON crudo de cada paso para poder diagnosticar
// contra la documentación de ubimia.
// ==========================================================

const STEP_ACCENTS = {
  1: "blue",
  2: "violet",
  3: "teal",
  4: "amber",
  5: "rose",
  6: "green",
};

const EMPTY_PARTICIPANT = {
  curp: "",
  rfc: "",
  firstName: "",
  secondName: "",
  surname: "",
  secondSurname: "",
  birthDate: "", // yyyy-mm-dd (input date)
  cellPhone: "",
  email: "",
};

const EMPTY_ADDRESS = {
  addressAntiquity: "",
  extNumber: "",
  intNumber: "",
  street: "",
  tipovivienda: "PROPIA",
  zipCode: "",
};

const EMPTY_EMPLOYMENT = {
  admissionDate: "", // yyyy-mm-dd
  currentEmployment: true,
  economicActivity: "", // catalog value (texto) de actividad_empresa
  descripcionEmpleo: "", // catalog value (texto) de descripcion_empresa
  ingresofijo: "",
  ingresovariable: "",
  nombreempresa: "",
  asalariadoPeriodicidad: "QUINCENAL",
  tipoIngreso: "ASALARIADO",
};

const EMPTY_BUREAU = {
  mortgageCredit: "0",
  tc: "0",
  authorization: "1",
  automotiveCredit: "0",
  tcDigits: "",
  aceptacionPrivacidad: "1",
};

function dateToMs(yyyyMmDd) {
  if (!yyyyMmDd) return 0;
  const ms = new Date(`${yyyyMmDd}T00:00:00`).getTime();
  return Number.isFinite(ms) ? ms : 0;
}

function catalogItems(result) {
  return result?.CONTAINER?.catalog?.catalogItems || [];
}

// ----------------------------------------------------------
// Componentes visuales chicos (mismo lenguaje que crearventa.js)
// ----------------------------------------------------------

function Icon({ name, size = 18, className = "" }) {
  const commonProps = {
    width: size,
    height: size,
    viewBox: "0 0 24 24",
    fill: "none",
    stroke: "currentColor",
    strokeWidth: 1.9,
    strokeLinecap: "round",
    strokeLinejoin: "round",
    className,
    "aria-hidden": true,
  };

  const icons = {
    user: (
      <>
        <path d="M20 21a8 8 0 0 0-16 0" />
        <circle cx="12" cy="7" r="4" />
      </>
    ),
    building: (
      <>
        <path d="M3 21h18" />
        <path d="M6 21V5l6-3 6 3v16" />
        <path d="M9 9h.01M15 9h.01M9 13h.01M15 13h.01M9 17h.01M15 17h.01" />
      </>
    ),
    home: (
      <>
        <path d="m3 11 9-8 9 8" />
        <path d="M5 10v10h14V10" />
      </>
    ),
    shield: (
      <>
        <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10Z" />
        <path d="m9 12 2 2 4-4" />
      </>
    ),
    check: <path d="m5 12 4 4L19 6" />,
    alert: (
      <>
        <path d="M10.3 3.7 1.8 18a2 2 0 0 0 1.7 3h17a2 2 0 0 0 1.7-3L13.7 3.7a2 2 0 0 0-3.4 0Z" />
        <path d="M12 9v4M12 17h.01" />
      </>
    ),
    file: (
      <>
        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8Z" />
        <path d="M14 2v6h6M8 13h8M8 17h6" />
      </>
    ),
    save: (
      <>
        <path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2Z" />
        <path d="M17 21v-8H7v8M7 3v5h8" />
      </>
    ),
    info: (
      <>
        <circle cx="12" cy="12" r="9" />
        <path d="M12 11v5M12 8h.01" />
      </>
    ),
    loader: <path d="M21 12a9 9 0 1 1-6.2-8.6" />,
    key: (
      <>
        <circle cx="8" cy="15" r="4" />
        <path d="m10.5 12.5 8-8M17 6l2 2M14 9l2 2" />
      </>
    ),
    pin: (
      <>
        <path d="M20 10c0 6-8 12-8 12s-8-6-8-12a8 8 0 0 1 16 0Z" />
        <circle cx="12" cy="10" r="3" />
      </>
    ),
    flask: (
      <>
        <path d="M9 2v6.5L4.5 18a2 2 0 0 0 1.8 3h11.4a2 2 0 0 0 1.8-3L15 8.5V2" />
        <path d="M8 2h8M7 15h10" />
      </>
    ),
    lock: (
      <>
        <rect x="4" y="10" width="16" height="10" rx="2" />
        <path d="M8 10V7a4 4 0 0 1 8 0v3" />
      </>
    ),
  };

  return <svg {...commonProps}>{icons[name] || icons.info}</svg>;
}

function FieldHelp({ children }) {
  if (!children) return null;
  return (
    <small className="nr-field-help">
      <Icon name="info" size={14} />
      <span>{children}</span>
    </small>
  );
}

function FieldLabel({ label, required = false, optional = false }) {
  return (
    <label className="form-label nr-field-label">
      <span>{label}</span>
      {required && <span className="nr-required">Requerido</span>}
      {optional && <span className="nr-optional">Opcional</span>}
    </label>
  );
}

function TextInput({ label, value, onChange, type = "text", help = "", placeholder = "", required = false, optional = false }) {
  return (
    <div className="nr-field">
      <FieldLabel label={label} required={required} optional={optional} />
      <input
        type={type}
        className={`form-control nr-control ${value ? "nr-control-filled" : ""}`}
        value={value ?? ""}
        placeholder={placeholder}
        aria-label={label}
        onChange={(event) => onChange(event.target.value)}
      />
      <FieldHelp>{help}</FieldHelp>
    </div>
  );
}

function SelectInput({ label, value, onChange, options, help = "", required = false, optional = false, disabled = false, loading = false }) {
  return (
    <div className="nr-field">
      <FieldLabel label={label} required={required} optional={optional} />
      <div className="nr-control-wrap">
        <select
          className={`form-select nr-control ${value ? "nr-control-filled" : ""}`}
          value={value ?? ""}
          disabled={disabled || loading}
          aria-label={label}
          onChange={(event) => onChange(event.target.value)}
        >
          {options.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
        {loading && <Icon name="loader" size={17} className="nr-spin nr-control-loader" />}
      </div>
      <FieldHelp>{help}</FieldHelp>
    </div>
  );
}

// Select alimentado directo por catalogItems de Prodesk ({key,value,description}).
function CatalogSelect({ label, items, selectedKey, onSelect, help = "", loading = false, required = false, optional = false }) {
  return (
    <div className="nr-field">
      <FieldLabel label={label} required={required} optional={optional} />
      <div className="nr-control-wrap">
        <select
          className={`form-select nr-control ${selectedKey ? "nr-control-filled" : ""}`}
          value={selectedKey ?? ""}
          disabled={loading}
          aria-label={label}
          onChange={(event) => {
            const key = event.target.value;
            const item = items.find((it) => String(it.key) === String(key)) || null;
            onSelect(key, item);
          }}
        >
          <option value="">{loading ? "Cargando opciones..." : "Seleccione una opción"}</option>
          {items.map((item) => (
            <option key={item.key} value={item.key}>
              {item.value}
            </option>
          ))}
        </select>
        {loading && <Icon name="loader" size={17} className="nr-spin nr-control-loader" />}
      </div>
      <FieldHelp>{help}</FieldHelp>
    </div>
  );
}

function SectionCard({ step, title, subtitle, icon, done = false, loading = false, children, action = null }) {
  const accent = STEP_ACCENTS[step] || "blue";
  return (
    <section className={`nr-card nr-section-card nr-accent-${accent} ${done ? "nr-section-complete" : ""}`}>
      <header className="nr-section-header">
        <div className="nr-section-heading">
          <div className={`nr-section-icon nr-badge-${accent}`}>
            <Icon name={icon} size={20} />
          </div>
          <div>
            <div className="nr-section-kicker">Paso {step}</div>
            <h5 className="nr-section-title">{title}</h5>
            {subtitle && <p className="nr-section-subtitle">{subtitle}</p>}
          </div>
        </div>
        <div className="d-flex align-items-center gap-2 flex-wrap justify-content-end">
          {loading && (
            <span className="nr-status nr-status-loading">
              <Icon name="loader" size={14} className="nr-spin" /> Cargando
            </span>
          )}
          {!loading && (
            <span className={`nr-status ${done ? "nr-status-success" : "nr-status-pending"}`}>
              <Icon name={done ? "check" : "alert"} size={14} />
              {done ? "Completo" : "Pendiente"}
            </span>
          )}
          {action}
        </div>
      </header>
      <div className="nr-section-body">{children}</div>
    </section>
  );
}

function ProgressHeader({ percent, tokenReady }) {
  return (
    <div className="nr-hero">
      <div className="nr-hero-icon">
        <Icon name="flask" size={26} />
      </div>
      <div className="nr-hero-body">
        <div className="nr-hero-kicker">Ambiente de pruebas · UAT</div>
        <h3 className="nr-hero-title">Prodesk (Originación NR)</h3>
        <p className="nr-hero-subtitle">Token → Catálogos → Código postal → Propuesta → Firma NAAT</p>
      </div>
      <div className="nr-hero-progress">
        <div className="nr-hero-progress-labels">
          <span>Progreso del flujo</span>
          <span>{percent}%</span>
        </div>
        <div className="nr-progress-track">
          <div className="nr-progress-fill" style={{ width: `${percent}%` }} />
        </div>
        <span className={`nr-status ${tokenReady ? "nr-status-success" : "nr-status-pending"} nr-hero-token-status`}>
          <Icon name={tokenReady ? "lock" : "alert"} size={13} />
          {tokenReady ? "Token activo" : "Sin token"}
        </span>
      </div>
    </div>
  );
}

// ----------------------------------------------------------
// Componente principal
// ----------------------------------------------------------

export default function ProdeskNR() {
  const prodesk = useProdesk();

  const [ventaId, setVentaId] = useState("");
  const [token, setToken] = useState("");

  const [catalogosGenerales, setCatalogosGenerales] = useState({
    sexo: [],
    estadoCivil: [],
    nacionalidad: [],
    paisNacimiento: [],
    ciudadNacimiento: [],
    actividadEmpresa: [],
    descripcionEmpresa: [],
  });

  const [participant, setParticipant] = useState(EMPTY_PARTICIPANT);
  const [genderItem, setGenderItem] = useState(null);
  const [maritalStatusItem, setMaritalStatusItem] = useState(null);
  const [nationalityItem, setNationalityItem] = useState(null);
  const [nativeCountryItem, setNativeCountryItem] = useState(null);
  const [nativeCityItem, setNativeCityItem] = useState(null);
  const [economicActivityItem, setEconomicActivityItem] = useState(null);
  const [loadingDescripcion, setLoadingDescripcion] = useState(false);

  const [address, setAddress] = useState(EMPTY_ADDRESS);
  const [cpResultado, setCpResultado] = useState(null); // { COLONIA:[], MUNICIPIO:[], ESTADO:[], CIUDAD:[] }
  const [neighborhoodItem, setNeighborhoodItem] = useState(null);

  const [employment, setEmployment] = useState(EMPTY_EMPLOYMENT);
  const [bureau, setBureau] = useState(EMPTY_BUREAU);

  const [folioCotizacionPortal, setFolioCotizacionPortal] = useState("NS000000105");
  const [folioProdesk, setFolioProdesk] = useState("");

  const [lastPropuestaResponse, setLastPropuestaResponse] = useState(null);
  const [lastFirmaResponse, setLastFirmaResponse] = useState(null);
  const [rawOpen, setRawOpen] = useState(false);

  const municipality = cpResultado?.MUNICIPIO?.[0]?.value || "";
  const state = cpResultado?.ESTADO?.[0]?.value || "";
  const city = cpResultado?.CIUDAD?.[0]?.value || "";

  const patchParticipant = useCallback((patch) => setParticipant((prev) => ({ ...prev, ...patch })), []);
  const patchAddress = useCallback((patch) => setAddress((prev) => ({ ...prev, ...patch })), []);
  const patchEmployment = useCallback((patch) => setEmployment((prev) => ({ ...prev, ...patch })), []);
  const patchBureau = useCallback((patch) => setBureau((prev) => ({ ...prev, ...patch })), []);

  // ------------------------------------------------------
  // Paso 1: Token
  // ------------------------------------------------------
  const handleObtenerToken = useCallback(async () => {
    try {
      const data = await prodesk.obtenerToken({ ventaId });
      setToken(data?.token || "");
      Swal.fire({ icon: "success", title: "Token Prodesk obtenido", timer: 1400, showConfirmButton: false });
    } catch (error) {
      Swal.fire({ icon: "error", title: "No se pudo obtener el token", text: error.message });
    }
  }, [prodesk, ventaId]);

  // ------------------------------------------------------
  // Paso 2: Catálogos generales (sexo, estado civil, nacionalidad,
  // país de nacimiento, ciudad de nacimiento, actividad de empresa)
  // ------------------------------------------------------
  const handleCargarCatalogosGenerales = useCallback(async () => {
    try {
      const ids = PRODESK_CATALOGOS_GENERALES;
      const [sexo, estadoCivil, nacionalidad, paisNacimiento, ciudadNacimiento, actividadEmpresa] = await Promise.all([
        prodesk.consultarCatalogo({ isCatalog: 1, valueFind: ids.sexo, ventaId, token }),
        prodesk.consultarCatalogo({ isCatalog: 1, valueFind: ids.estadoCivil, ventaId, token }),
        prodesk.consultarCatalogo({ isCatalog: 1, valueFind: ids.nacionalidad, ventaId, token }),
        prodesk.consultarCatalogo({ isCatalog: 1, valueFind: ids.paisNacimiento, ventaId, token }),
        prodesk.consultarCatalogo({ isCatalog: 1, valueFind: ids.ciudadNacimiento, ventaId, token }),
        prodesk.consultarCatalogo({ isCatalog: 1, valueFind: ids.actividadEmpresa, ventaId, token }),
      ]);

      setCatalogosGenerales((prev) => ({
        ...prev,
        sexo: catalogItems(sexo),
        estadoCivil: catalogItems(estadoCivil),
        nacionalidad: catalogItems(nacionalidad),
        paisNacimiento: catalogItems(paisNacimiento),
        ciudadNacimiento: catalogItems(ciudadNacimiento),
        actividadEmpresa: catalogItems(actividadEmpresa),
      }));

      Swal.fire({ icon: "success", title: "Catálogos generales cargados", timer: 1200, showConfirmButton: false });
    } catch (error) {
      Swal.fire({ icon: "error", title: "Error al cargar catálogos", text: error.message });
    }
  }, [prodesk, ventaId, token]);

  // Descripción de empresa depende de la actividad de empresa
  // seleccionada. Se carga sola en cuanto cambia la actividad, para
  // no depender de un botón aparte.
  useEffect(() => {
    if (!economicActivityItem || !token) {
      return;
    }

    let cancelled = false;

    (async () => {
      setLoadingDescripcion(true);
      try {
        const result = await prodeskConsultarCatalogo({
          isCatalog: 1,
          valueFind: PRODESK_CATALOGOS_GENERALES.descripcionEmpresa,
          campo: economicActivityItem.key,
          ventaId,
          token,
        });
        if (!cancelled) {
          setCatalogosGenerales((prev) => ({ ...prev, descripcionEmpresa: catalogItems(result) }));
        }
      } catch (error) {
        if (!cancelled) {
          Swal.fire({ icon: "error", title: "Error al cargar descripción de empresa", text: error.message });
        }
      } finally {
        if (!cancelled) {
          setLoadingDescripcion(false);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [economicActivityItem, token]);

  // ------------------------------------------------------
  // Paso 3: Código postal (Colonia / Municipio / Estado / Ciudad)
  // ------------------------------------------------------
  const handleConsultarCP = useCallback(async () => {
    if (!address.zipCode) {
      Swal.fire({ icon: "warning", title: "Captura un código postal" });
      return;
    }
    try {
      const data = await prodesk.consultarCodigoPostal({ zipCode: address.zipCode, ventaId, token });
      setCpResultado(data);
      setNeighborhoodItem(null);
      Swal.fire({ icon: "success", title: "Código postal consultado", timer: 1200, showConfirmButton: false });
    } catch (error) {
      Swal.fire({ icon: "error", title: "Error al consultar CP", text: error.message });
    }
  }, [prodesk, address.zipCode, ventaId, token]);

  // ------------------------------------------------------
  // Paso 4: Guardar propuesta
  // ------------------------------------------------------
  const propuestaPayload = useMemo(() => {
    const direccion = buildProdeskAddress({
      addressAntiquity: address.addressAntiquity,
      city,
      tipovivienda: address.tipovivienda,
      extNumber: address.extNumber,
      intNumber: address.intNumber,
      municipality,
      neighborhoodCatalogItem: neighborhoodItem,
      state,
      street: address.street,
      zipCode: address.zipCode,
    });

    const empleo = buildProdeskEmployment({
      admissionDateMs: dateToMs(employment.admissionDate),
      currentEmployment: employment.currentEmployment,
      economicActivity: economicActivityItem?.value || "",
      descripcionEmpleo: employment.descripcionEmpleo,
      ingresoFijo: employment.ingresofijo,
      ingresoVariable: employment.ingresovariable,
      nombreEmpresa: employment.nombreempresa,
      asalariadoPeriodicidad: employment.asalariadoPeriodicidad,
      tipoIngreso: employment.tipoIngreso,
    });

    const buro = buildProdeskCreditsBureau({
      mortgageCredit: bureau.mortgageCredit,
      tc: bureau.tc,
      authorization: bureau.authorization,
      automotiveCredit: bureau.automotiveCredit,
      tcDigits: bureau.tcDigits,
      aceptacionPrivacidad: bureau.aceptacionPrivacidad,
    });

    const participantePayload = buildProdeskParticipant({
      curp: participant.curp,
      rfc: participant.rfc,
      firstName: participant.firstName,
      secondName: participant.secondName,
      surname: participant.surname,
      secondSurname: participant.secondSurname,
      birthDateMs: dateToMs(participant.birthDate),
      cellPhone: participant.cellPhone,
      email: participant.email,
      genderCatalogItem: genderItem,
      maritalStatusCatalogItem: maritalStatusItem,
      nationalityCatalogItem: nationalityItem,
      nativeCity: nativeCityItem?.value || "",
      nativeCountry: nativeCountryItem?.value || "",
      addresses: [direccion],
      employments: [empleo],
      creditsBureau: [buro],
    });

    return buildProdeskPropuesta({
      participants: [participantePayload],
      folioCotizacionPortal,
    });
  }, [
    address, city, municipality, state, neighborhoodItem,
    employment, economicActivityItem, bureau,
    participant, genderItem, maritalStatusItem, nationalityItem, nativeCityItem, nativeCountryItem,
    folioCotizacionPortal,
  ]);

  const handleGuardarPropuesta = useCallback(async () => {
    try {
      const data = await prodesk.guardarPropuesta({ propuesta: propuestaPayload, ventaId, token });
      setLastPropuestaResponse(data);
      setFolioProdesk(data?.folio || "");
      Swal.fire({ icon: "success", title: `Propuesta guardada · Folio ${data?.folio || "-"}` });
    } catch (error) {
      setLastPropuestaResponse(error?.payload || null);
      Swal.fire({ icon: "error", title: "Error al guardar propuesta", text: error.message });
    }
  }, [prodesk, propuestaPayload, ventaId, token]);

  // ------------------------------------------------------
  // Paso 5: Firma NAAT
  // ------------------------------------------------------
  const handleGenerarFirmaNaat = useCallback(async () => {
    if (!folioProdesk) {
      Swal.fire({ icon: "warning", title: "Primero guarda la propuesta para obtener un folio" });
      return;
    }
    try {
      const data = await prodesk.generarFirmaNaat({ folio: folioProdesk, ventaId, token });
      setLastFirmaResponse(data);
      Swal.fire({ icon: "success", title: "Firma NAAT solicitada", text: "Revisa la respuesta técnica para el detalle." });
    } catch (error) {
      setLastFirmaResponse(error?.payload || null);
      Swal.fire({ icon: "error", title: "Error al solicitar firma NAAT", text: error.message });
    }
  }, [prodesk, folioProdesk, ventaId, token]);

  // ------------------------------------------------------
  // Progreso general (solo visual, no bloquea nada)
  // ------------------------------------------------------
  const stepsDone = useMemo(
    () => [
      Boolean(token),
      Boolean(genderItem && maritalStatusItem && nationalityItem),
      Boolean(neighborhoodItem),
      Boolean(economicActivityItem),
      Boolean(bureau.aceptacionPrivacidad === "1"),
      Boolean(folioProdesk),
    ],
    [token, genderItem, maritalStatusItem, nationalityItem, neighborhoodItem, economicActivityItem, bureau, folioProdesk],
  );
  const progressPercent = Math.round((stepsDone.filter(Boolean).length / stepsDone.length) * 100);

  return (
    <div className="prodesk-page container py-4">
      <ProgressHeader percent={progressPercent} tokenReady={Boolean(token)} />

      <div className="nr-field nr-venta-id-field">
        <TextInput label="venta_id (opcional, solo para logs)" value={ventaId} onChange={setVentaId} placeholder="Ej. 1234" />
      </div>

      {prodesk.lastError && (
        <div className="alert alert-danger nr-alert" role="alert">
          <Icon name="alert" size={16} />
          <span>{prodesk.lastError}</span>
        </div>
      )}

      <SectionCard
        step={1}
        title="Token"
        subtitle="Se obtiene con el usuario/password configurados en el backend (mismos que Procotiza). Por seguridad, no se muestra en pantalla."
        icon="key"
        done={Boolean(token)}
        loading={prodesk.loading.token}
        action={
          <button type="button" className="btn btn-outline-primary nr-btn" onClick={handleObtenerToken} disabled={prodesk.loading.token}>
            <Icon name={prodesk.loading.token ? "loader" : "key"} size={17} className={prodesk.loading.token ? "nr-spin" : ""} />
            {token ? "Renovar token" : "Obtener token"}
          </button>
        }
      >
        <div className="nr-token-state">
          <div className={`nr-token-pill ${token ? "nr-token-pill-active" : ""}`}>
            <Icon name={token ? "lock" : "alert"} size={16} />
            {token ? (
              <span>
                Token oculto — activo en memoria (termina en <strong>{token.slice(-4)}</strong>)
              </span>
            ) : (
              <span>Aún no se ha obtenido ningún token</span>
            )}
          </div>
          <FieldHelp>No se imprime el valor completo en pantalla ni en el JSON técnico, para no exponerlo por accidente en una captura de pantalla.</FieldHelp>
        </div>
      </SectionCard>

      <SectionCard
        step={2}
        title="Datos del solicitante"
        subtitle="Catálogos generales de Prodesk (sexo, estado civil, nacionalidad, país/ciudad de nacimiento, actividad de empresa)."
        icon="user"
        done={Boolean(genderItem && maritalStatusItem && nationalityItem)}
        loading={prodesk.loading.catalogo}
        action={
          <button type="button" className="btn btn-outline-primary nr-btn" onClick={handleCargarCatalogosGenerales} disabled={prodesk.loading.catalogo}>
            <Icon name={prodesk.loading.catalogo ? "loader" : "info"} size={17} className={prodesk.loading.catalogo ? "nr-spin" : ""} />
            Cargar catálogos generales
          </button>
        }
      >
        <div className="row g-3">
          <div className="col-md-4"><TextInput label="CURP" value={participant.curp} onChange={(v) => patchParticipant({ curp: v })} required /></div>
          <div className="col-md-4"><TextInput label="RFC" value={participant.rfc} onChange={(v) => patchParticipant({ rfc: v })} required /></div>
          <div className="col-md-4"><TextInput label="Fecha de nacimiento" type="date" value={participant.birthDate} onChange={(v) => patchParticipant({ birthDate: v })} required /></div>
          <div className="col-md-4"><TextInput label="Primer nombre" value={participant.firstName} onChange={(v) => patchParticipant({ firstName: v })} required /></div>
          <div className="col-md-4"><TextInput label="Segundo nombre" value={participant.secondName} onChange={(v) => patchParticipant({ secondName: v })} optional /></div>
          <div className="col-md-4"><TextInput label="Apellido paterno" value={participant.surname} onChange={(v) => patchParticipant({ surname: v })} required /></div>
          <div className="col-md-4"><TextInput label="Apellido materno" value={participant.secondSurname} onChange={(v) => patchParticipant({ secondSurname: v })} optional /></div>
          <div className="col-md-4"><TextInput label="Celular" value={participant.cellPhone} onChange={(v) => patchParticipant({ cellPhone: v })} required /></div>
          <div className="col-md-4"><TextInput label="Correo" type="email" value={participant.email} onChange={(v) => patchParticipant({ email: v })} required /></div>

          <div className="col-md-4">
            <CatalogSelect label="Sexo" items={catalogosGenerales.sexo} selectedKey={genderItem?.key} onSelect={(_, item) => setGenderItem(item)} required />
          </div>
          <div className="col-md-4">
            <CatalogSelect label="Estado civil" items={catalogosGenerales.estadoCivil} selectedKey={maritalStatusItem?.key} onSelect={(_, item) => setMaritalStatusItem(item)} required />
          </div>
          <div className="col-md-4">
            <CatalogSelect label="Nacionalidad" items={catalogosGenerales.nacionalidad} selectedKey={nationalityItem?.key} onSelect={(_, item) => setNationalityItem(item)} required />
          </div>
          <div className="col-md-6">
            <CatalogSelect label="País de nacimiento" items={catalogosGenerales.paisNacimiento} selectedKey={nativeCountryItem?.key} onSelect={(_, item) => setNativeCountryItem(item)} required />
          </div>
          <div className="col-md-6">
            <CatalogSelect label="Ciudad de nacimiento" items={catalogosGenerales.ciudadNacimiento} selectedKey={nativeCityItem?.key} onSelect={(_, item) => setNativeCityItem(item)} required />
          </div>
        </div>
      </SectionCard>

      <SectionCard
        step={3}
        title="Domicilio"
        subtitle="Colonia/Municipio/Estado/Ciudad se obtienen consultando el código postal en Prodesk."
        icon="home"
        done={Boolean(neighborhoodItem)}
        loading={prodesk.loading.codigoPostal}
        action={
          <button type="button" className="btn btn-outline-primary nr-btn" onClick={handleConsultarCP} disabled={prodesk.loading.codigoPostal}>
            <Icon name={prodesk.loading.codigoPostal ? "loader" : "pin"} size={17} className={prodesk.loading.codigoPostal ? "nr-spin" : ""} />
            Consultar CP
          </button>
        }
      >
        <div className="row g-3">
          <div className="col-md-3"><TextInput label="Código postal" value={address.zipCode} onChange={(v) => patchAddress({ zipCode: v })} required /></div>
          <div className="col-md-3">
            <CatalogSelect label="Colonia" items={cpResultado?.COLONIA || []} selectedKey={neighborhoodItem?.key} onSelect={(_, item) => setNeighborhoodItem(item)} required />
          </div>
          <div className="col-md-2"><TextInput label="Municipio" value={municipality} onChange={() => {}} help="Se llena solo tras consultar el CP." /></div>
          <div className="col-md-2"><TextInput label="Estado" value={state} onChange={() => {}} /></div>
          <div className="col-md-2"><TextInput label="Ciudad" value={city} onChange={() => {}} /></div>

          <div className="col-md-4"><TextInput label="Calle" value={address.street} onChange={(v) => patchAddress({ street: v })} required /></div>
          <div className="col-md-2"><TextInput label="No. exterior" value={address.extNumber} onChange={(v) => patchAddress({ extNumber: v })} required /></div>
          <div className="col-md-2"><TextInput label="No. interior" value={address.intNumber} onChange={(v) => patchAddress({ intNumber: v })} optional /></div>
          <div className="col-md-2"><TextInput label="Antigüedad (años)" type="number" value={address.addressAntiquity} onChange={(v) => patchAddress({ addressAntiquity: v })} required /></div>
          <div className="col-md-2">
            <SelectInput
              label="Tipo de vivienda"
              value={address.tipovivienda}
              onChange={(v) => patchAddress({ tipovivienda: v })}
              options={[{ value: "PROPIA", label: "Propia" }, { value: "RENTADA", label: "Rentada" }]}
            />
          </div>
        </div>
      </SectionCard>

      <SectionCard
        step={4}
        title="Empleo"
        subtitle="Actividad de empresa viene de Catalogs.svc (83); descripción de empleo se carga sola según la actividad (82)."
        icon="building"
        done={Boolean(economicActivityItem)}
      >
        <div className="row g-3">
          <div className="col-md-6">
            <CatalogSelect label="Actividad de empresa" items={catalogosGenerales.actividadEmpresa} selectedKey={economicActivityItem?.key} onSelect={(_, item) => setEconomicActivityItem(item)} required help="Ve al Paso 2 y pulsa “Cargar catálogos generales” si esta lista está vacía." />
          </div>
          <div className="col-md-6">
            <SelectInput
              label="Descripción de empleo"
              value={employment.descripcionEmpleo}
              onChange={(v) => patchEmployment({ descripcionEmpleo: v })}
              loading={loadingDescripcion}
              disabled={!economicActivityItem}
              options={[{ value: "", label: economicActivityItem ? "Seleccione una opción" : "Elige primero una actividad" }, ...catalogosGenerales.descripcionEmpresa.map((it) => ({ value: it.value, label: it.value }))]}
              help={economicActivityItem ? "Se carga automáticamente al elegir la actividad." : ""}
            />
          </div>
        </div>

        <div className="nr-subgroup-title">Datos laborales</div>
        <div className="row g-3">
          <div className="col-md-4"><TextInput label="Nombre de la empresa" value={employment.nombreempresa} onChange={(v) => patchEmployment({ nombreempresa: v })} required /></div>
          <div className="col-md-4"><TextInput label="Fecha de ingreso" type="date" value={employment.admissionDate} onChange={(v) => patchEmployment({ admissionDate: v })} required /></div>
          <div className="col-md-4">
            <SelectInput
              label="Periodicidad"
              value={employment.asalariadoPeriodicidad}
              onChange={(v) => patchEmployment({ asalariadoPeriodicidad: v })}
              options={["MENSUAL", "QUINCENAL", "SEMANAL"].map((v) => ({ value: v, label: v }))}
            />
          </div>
          <div className="col-md-4">
            <SelectInput
              label="Tipo de ingreso"
              value={employment.tipoIngreso}
              onChange={(v) => patchEmployment({ tipoIngreso: v })}
              options={["ASALARIADO", "INDEPENDIENTE"].map((v) => ({ value: v, label: v }))}
            />
          </div>
          <div className="col-md-4"><TextInput label="Ingreso fijo" type="number" value={employment.ingresofijo} onChange={(v) => patchEmployment({ ingresofijo: v })} required /></div>
          <div className="col-md-4"><TextInput label="Ingreso variable" type="number" value={employment.ingresovariable} onChange={(v) => patchEmployment({ ingresovariable: v })} optional /></div>
        </div>
      </SectionCard>

      <SectionCard step={5} title="Buró de crédito" subtitle="Indicadores y autorización de consulta." icon="shield" done={bureau.aceptacionPrivacidad === "1"}>
        <div className="row g-3">
          <div className="col-md-3">
            <SelectInput label="Crédito hipotecario" value={bureau.mortgageCredit} onChange={(v) => patchBureau({ mortgageCredit: v })} options={[{ value: "1", label: "Sí" }, { value: "0", label: "No" }]} />
          </div>
          <div className="col-md-3">
            <SelectInput label="Tarjeta de crédito" value={bureau.tc} onChange={(v) => patchBureau({ tc: v })} options={[{ value: "1", label: "Sí" }, { value: "0", label: "No" }]} />
          </div>
          <div className="col-md-3">
            <SelectInput label="Crédito automotriz" value={bureau.automotiveCredit} onChange={(v) => patchBureau({ automotiveCredit: v })} options={[{ value: "1", label: "Sí" }, { value: "0", label: "No" }]} />
          </div>
          <div className="col-md-3"><TextInput label="Últimos 4 dígitos TC" value={bureau.tcDigits} onChange={(v) => patchBureau({ tcDigits: v })} optional /></div>
          <div className="col-md-3">
            <SelectInput label="Autoriza consulta de buró" value={bureau.aceptacionPrivacidad} onChange={(v) => patchBureau({ aceptacionPrivacidad: v })} options={[{ value: "1", label: "Sí" }, { value: "0", label: "No" }]} required />
          </div>
        </div>
      </SectionCard>

      <SectionCard
        step={6}
        title="Guardar propuesta y firma NAAT"
        subtitle="Envía todo lo capturado a Proposal.svc/json y, con el folio devuelto, solicita la firma NAAT."
        icon="file"
        done={Boolean(folioProdesk)}
        loading={prodesk.loading.propuesta || prodesk.loading.firmaNaat}
      >
        <div className="row g-3 mb-3">
          <div className="col-md-4"><TextInput label="folioCotizacionPortal" value={folioCotizacionPortal} onChange={setFolioCotizacionPortal} /></div>
          <div className="col-md-4"><TextInput label="Folio Prodesk (respuesta)" value={folioProdesk} onChange={() => {}} help="Se llena solo al guardar la propuesta." /></div>
        </div>

        <div className="d-flex gap-2 flex-wrap">
          <button type="button" className="btn btn-outline-primary nr-btn" onClick={handleGuardarPropuesta} disabled={prodesk.loading.propuesta}>
            <Icon name={prodesk.loading.propuesta ? "loader" : "save"} size={17} className={prodesk.loading.propuesta ? "nr-spin" : ""} />
            Guardar propuesta en Prodesk
          </button>
          <button type="button" className="btn btn-outline-success nr-btn" onClick={handleGenerarFirmaNaat} disabled={prodesk.loading.firmaNaat || !folioProdesk}>
            <Icon name={prodesk.loading.firmaNaat ? "loader" : "check"} size={17} className={prodesk.loading.firmaNaat ? "nr-spin" : ""} />
            Generar firma NAAT
          </button>
          <button type="button" className="btn btn-outline-secondary nr-btn ms-md-auto" onClick={() => setRawOpen((prev) => !prev)}>
            <Icon name="info" size={17} /> {rawOpen ? "Ocultar JSON técnico" : "Ver JSON técnico"}
          </button>
        </div>

        {rawOpen && (
          <pre className="nr-json mt-3">
            {JSON.stringify(
              {
                token: token ? `••••••••${token.slice(-4)}` : "",
                propuestaEnviada: propuestaPayload,
                respuestaPropuesta: lastPropuestaResponse,
                respuestaFirmaNaat: lastFirmaResponse,
              },
              null,
              2,
            )}
          </pre>
        )}
      </SectionCard>
    </div>
  );
}